/**
 * BaseInput - Foundational input component with styling
 *
 * This is a low-level component. Prefer using TextField, EmailField, etc.
 */

import { forwardRef } from 'react';

import { cn } from '../utils';

export interface BaseInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  /** Whether the input has an error state */
  hasError?: boolean;
  /** Icon to show on the left side */
  iconLeft?: React.ReactNode;
  /** Icon or element to show on the right side */
  iconRight?: React.ReactNode;
}

export const BaseInput = forwardRef<HTMLInputElement, BaseInputProps>(
  ({ hasError, iconLeft, iconRight, className, ...props }, ref) => {
    return (
      <div className="relative">
        {iconLeft && (
          <span className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-gray-400 dark:text-zinc-500">
            {iconLeft}
          </span>
        )}
        <input
          ref={ref}
          className={cn(
            'w-full rounded-lg border px-4 py-3 text-sm transition-all sm:py-2.5',
            'bg-gray-50 text-gray-900 placeholder-gray-400',
            'dark:bg-[#18181b] dark:text-white dark:placeholder-zinc-500',
            'focus:outline-none focus:ring-1',
            iconLeft && 'pl-10',
            iconRight && 'pr-10',
            hasError
              ? 'border-red-500 focus:border-red-500 focus:ring-red-500'
              : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500 dark:border-white/[0.06]',
            props.disabled && 'cursor-not-allowed opacity-50',
            className
          )}
          {...props}
        />
        {iconRight && (
          <span className="absolute inset-y-0 right-3 flex items-center">{iconRight}</span>
        )}
      </div>
    );
  }
);

BaseInput.displayName = 'BaseInput';
